import React from 'react';
import { notDesktop } from '@zola-helpers/client';
import { ButtonV2, DownloadIcon, DeleteIcon } from '@zola/zola-ui';
import type { WUploadedImageView } from '@zola/svc-web-api-ts-client';

import ImageUpload from 'components/common/ImageUpload';
import Loader from 'components/common/ui/Loader';

import iconUpload from 'assets/images/guestlist/iconUpload.png';

import {
  ConfigureAddressCollectionPhotoUploaderDispatchProps,
  ConfigureAddressCollectionPhotoUploaderStateProps,
} from 'components/manage/guestlist/addressCollection/ConfigureAddressCollectionPhotoUploader';

import styles from './ConfigureAddressCollectionPhotoUploader.module.less';

const MIN_IMG_HEIGHT_WIDTH = 600;

type UploadButtonProps = {
  busy: boolean;
  editable: boolean;
  onClick: () => void;
  tempImg: ConfigureAddressCollectionPhotoUploaderStateProps['tempImg'];
  guestCollectionImg: ConfigureAddressCollectionPhotoUploaderStateProps['guestCollectionImg'];
  clearImage: ConfigureAddressCollectionPhotoUploaderDispatchProps['clearImage'];
};

const UploadInterface: React.FC<UploadButtonProps> = ({
  busy,
  onClick,
  tempImg,
  guestCollectionImg,
  clearImage,
}) => {
  if (busy) return <Loader />;

  if (tempImg?.uuid || guestCollectionImg) {
    const imgSrc = tempImg?.url || guestCollectionImg;
    return (
      <div className={styles.uploadedContainer}>
        <img className={styles.img} src={imgSrc} alt="User uploaded file" />
        <div className={styles.buttons}>
          <button className={styles.imgActionCta} onClick={onClick}>
            <DownloadIcon className={styles.icon} flip="vertical" />
            Change Photo
          </button>
          <button className={styles.imgActionCta} onClick={clearImage}>
            <DeleteIcon className={styles.icon} />
            Remove Photo
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <img className={styles.iconUpload} src={iconUpload} alt="Click here to upload file" />
      <p className={styles.tip}>
        <span role="img" aria-label="Light bulb">
          💡
        </span>
        <strong>Pro Tip:</strong> A horizontal photo will look best.
      </p>
      <ButtonV2
        className={styles.browseBtn}
        onClick={onClick}
        fullWidth={notDesktop()}
        variant={notDesktop() ? 'primary' : 'secondary'}
      >
        Browse Photos
      </ButtonV2>
    </div>
  );
};

type ConfigureAddressCollectionPhotoUploaderProps = ConfigureAddressCollectionPhotoUploaderStateProps &
  ConfigureAddressCollectionPhotoUploaderDispatchProps & {
    onImageSave?: (zolaFile: WUploadedImageView) => void;
  };

const ConfigureAddressCollectionPhotoUploader: React.FC<ConfigureAddressCollectionPhotoUploaderProps> = ({
  tempImg,
  guestCollectionImg,
  onImageSave,
  clearImage,
}) => {
  return (
    <ImageUpload
      imageSizeRequirements={{
        minWidth: MIN_IMG_HEIGHT_WIDTH,
        minHeight: MIN_IMG_HEIGHT_WIDTH,
        minCrop: {
          height: MIN_IMG_HEIGHT_WIDTH,
          width: MIN_IMG_HEIGHT_WIDTH,
        },
      }}
      allowedFileTypes={['image/jpeg', 'image/png', 'image/gif']}
      imageElement={tempImg}
      onUploadComplete={onImageSave}
      resetAfterSubmit
      renderButton={(busy: boolean, editable: boolean, onClick: () => void): JSX.Element => (
        <UploadInterface
          {...{ busy, editable, onClick, tempImg, guestCollectionImg, clearImage }}
        />
      )}
    />
  );
};

export default ConfigureAddressCollectionPhotoUploader;



// WEBPACK FOOTER //
// ./src/components/manage/guestlist/addressCollection/ConfigureAddressCollectionPhotoUploader/ConfigureAddressCollectionPhotoUploader.tsx